import { FC, useState } from 'react';
import { ProgramToken } from 'types';
import { useParams } from 'react-router-dom';
import { useTransactions } from 'store/transactions/useTransactions';
import {
  Heading,
  Box,
  FormControl,
  FormLabel,
  Input,
  Button,
  Text,
  Flex,
  GridItem,
  SimpleGrid,
  Grid
} from '@chakra-ui/react';
import SelectTokenInput from 'components/onboarding/deployment/selectTokens/selectTokenInput';
import { FormError } from 'components/onboarding/form/FormError';
import { yupResolver } from '@hookform/resolvers/yup';
import { fundBonusTokenSchema } from 'components/onboarding/deployment/formSchema';
import { IFundBonusTokenForm } from 'components/onboarding/deployment/type';
import { useTokenBalance } from 'hooks/useTokenBalance';
import { useForm } from 'react-hook-form';
import {
  mapPowerSwitchStatusToDescription,
  PowerSwitchStatus
} from 'api/blockchain/rewardProgram';
import { ethers } from 'ethers';
import { useTokenList } from 'hooks/query/useTokenList';

type Props = {
  bonusTokens: ProgramToken[];
  rewardProgramDataMethod: string;
  programState?: PowerSwitchStatus;
  aludelAbi: ethers.ContractInterface;
};

export const FundBonusTokensForm: FC<Props> = ({
  bonusTokens,
  rewardProgramDataMethod,
  programState,
  aludelAbi
}) => {
  const { registerBonusToken, fundBonusToken } = useTransactions();
  const { rewardProgramAddress } =
    useParams<{ rewardProgramAddress: string }>();
  const { data: tokenList = [] } = useTokenList();

  const [selectedBonusToken, setSelectedBonusToken] = useState<ProgramToken>();

  const {
    watch,
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<IFundBonusTokenForm>({
    resolver: yupResolver(fundBonusTokenSchema),
    mode: 'onBlur',
    defaultValues: {
      bonusTokenAmount: 0
    }
  });

  const { insufficientBalance, nullAmount } = useTokenBalance(
    selectedBonusToken,
    watch('bonusTokenAmount', 0).toString()
  );
  const bonusTokenAmount = watch('bonusTokenAmount', 0);

  const renderError = () => {
    if (insufficientBalance && !nullAmount) {
      return <FormError>Insufficient balance</FormError>;
    }

    if (errors.bonusTokenAmount) {
      return <FormError>{errors.bonusTokenAmount.message}</FormError>;
    }
  };

  const fund = async ({ bonusTokenAmount }: { bonusTokenAmount: number }) => {
    if (selectedBonusToken) {
      const bonusToken = { ...selectedBonusToken, amount: bonusTokenAmount };
      const registerBonusTokenStatus = await registerBonusToken(
        rewardProgramAddress,
        bonusToken,
        rewardProgramDataMethod,
        aludelAbi
      );

      // Only fund bonus token if registration succeeds
      if (bonusTokenAmount > 0) {
        if (registerBonusTokenStatus) {
          await fundBonusToken(
            rewardProgramAddress,
            bonusToken,
            rewardProgramDataMethod,
            aludelAbi
          );
        }
      }
    }
  };

  return (
    <>
      <Heading size='md' mb={6}>
        Fund Bonus Token
      </Heading>
      <Text color='gray.200' mb={6}>
        Select a token and the amount of that token you'd like to add to the
        reward pool of your program. If the token hasn't been registered before,
        it will require an additional transaction. The registration process is
        only required once per bonus token address.
      </Text>
      <Box p={4} background='gray.700' borderRadius={8} mb={8}>
        <Heading size='sm' mb={1}>
          Registered bonus tokens
        </Heading>
        <Grid
          templateColumns={['repeat(1, 1fr)', null, null, 'repeat(5, 1fr)']}
          gap={[null, null, null, 5]}
          width='fit-content'
          alignItems={'center'}
        >
          {bonusTokens.length > 0 &&
            bonusTokens.map((token) => (
              <>
                <GridItem colSpan={1}>
                  <Text isTruncated>{token.tokenSymbol}</Text>
                </GridItem>
                <GridItem colSpan={3}>
                  <Text>{token.address}</Text>
                </GridItem>
                <GridItem colSpan={1}>
                  <Button
                    borderRadius={'lg'}
                    onClick={() => setSelectedBonusToken(token)}
                  >
                    Load
                  </Button>
                </GridItem>
              </>
            ))}
        </Grid>
        {bonusTokens.length === 0 && <Text>No bonus tokens registered</Text>}
      </Box>
      <SimpleGrid columns={[1, 2, 3]} gap={6}>
        <GridItem>
          <SelectTokenInput
            tokenList={tokenList}
            setSelectedToken={setSelectedBonusToken}
            selectedToken={selectedBonusToken}
            required
          />
        </GridItem>
        <GridItem>
          <FormControl
            isInvalid={!!errors.bonusTokenAmount || insufficientBalance}
            isDisabled={!selectedBonusToken}
          >
            <FormLabel>Enter amount (optional)</FormLabel>
            <Input
              id='bonusTokenAmount'
              type='number'
              step='any'
              borderColor={'gray.300'}
              placeholder='0'
              {...register('bonusTokenAmount')}
            />
            {renderError()}
          </FormControl>
        </GridItem>

        <GridItem>
          <Flex height='100%' alignItems='flex-end'>
            <Button
              borderRadius={'lg'}
              px={6}
              disabled={
                !selectedBonusToken ||
                programState === PowerSwitchStatus.OFFLINE ||
                programState === PowerSwitchStatus.SHUTDOWN ||
                insufficientBalance
              }
              onClick={handleSubmit((d) => fund(d))}
            >
              {programState === PowerSwitchStatus.OFFLINE ||
              programState === PowerSwitchStatus.SHUTDOWN
                ? mapPowerSwitchStatusToDescription[programState]
                : bonusTokenAmount > 0
                ? 'Fund'
                : 'Register'}
            </Button>
          </Flex>
        </GridItem>
      </SimpleGrid>
    </>
  );
};
